import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  amountInWordsGbp,
  amountInWordsInr,
  computePaymentInvoiceTotals,
  formatGbp,
  formatPaymentInvoiceDate,
  paymentLineAmount,
  PAYMENT_COMPANY,
} from '../utils/paymentInvoiceModelDefaults';

const ACCENT = '#0d9488';
const ACCENT_SOFT = '#ecfdf5';
const ACCENT_HEADER = '#d1fae5';
const INK = '#0f172a';
const SLATE = '#475569';
const BORDER = 'rgba(15, 23, 42, 0.1)';

function AddressBlock({ title, name, lines, accent }) {
  return (
    <Box sx={{ flex: 1, minWidth: 0, p: 2.5 }}>
      {title && (
        <Typography
          sx={{
            fontSize: '0.72rem',
            fontWeight: 800,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: accent,
            mb: 1,
          }}
        >
          {title}
        </Typography>
      )}
      <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: INK, mb: 1, lineHeight: 1.35 }}>
        {name || '—'}
      </Typography>
      {lines.filter(Boolean).map((line) => (
        <Typography key={line} sx={{ fontSize: '0.82rem', color: SLATE, lineHeight: 1.55 }}>
          {line}
        </Typography>
      ))}
    </Box>
  );
}

export default function PaymentInvoiceTemplatePreview({ model }) {
  const { subtotal, total, inrAmount } = computePaymentInvoiceTotals(model);
  const logoSrc = PAYMENT_COMPANY.logoSrc;

  return (
    <Box
      sx={{
        bgcolor: '#fff',
        borderRadius: '20px',
        overflow: 'hidden',
        boxShadow: '0 28px 70px rgba(15, 23, 42, 0.14)',
        border: `1px solid ${BORDER}`,
        fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif',
        color: INK,
      }}
    >
      <Box
        sx={{
          height: 4,
          background: `linear-gradient(90deg, ${ACCENT} 0%, #14b8a6 50%, #2dd4bf 100%)`,
        }}
      />

      <Box sx={{ px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 3.5 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            mb: 3,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                p: 1.25,
                borderRadius: '16px',
                bgcolor: ACCENT_SOFT,
                border: `1px solid rgba(13, 148, 136, 0.15)`,
                boxShadow: '0 8px 24px rgba(13, 148, 136, 0.12)',
              }}
            >
              <Box
                component="img"
                src={logoSrc}
                alt={model.companyName || PAYMENT_COMPANY.name}
                sx={{ height: 52, width: 'auto', display: 'block' }}
              />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: '0.95rem',
                  fontWeight: 800,
                  color: ACCENT,
                  letterSpacing: '0.04em',
                }}
              >
                {model.companyName || PAYMENT_COMPANY.name}
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: SLATE, mt: 0.25 }}>
                Export &amp; service billing
              </Typography>
            </Box>
          </Box>

          <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
            <Typography
              sx={{
                fontFamily: '"Georgia", "Times New Roman", serif',
                fontSize: 'clamp(1.75rem, 4vw, 2.35rem)',
                fontWeight: 700,
                letterSpacing: '0.14em',
                color: INK,
                lineHeight: 1,
                mb: 1.25,
              }}
            >
              INVOICE
            </Typography>
            <Box sx={{ display: 'grid', gap: 0.5 }}>
              <Typography sx={{ fontSize: '0.84rem', color: SLATE }}>
                <Box component="span" sx={{ fontWeight: 700, color: INK }}>
                  Date:
                </Box>{' '}
                {formatPaymentInvoiceDate(model.issueDate)}
              </Typography>
              <Typography sx={{ fontSize: '0.84rem', color: SLATE }}>
                <Box component="span" sx={{ fontWeight: 700, color: INK }}>
                  Invoice No:
                </Box>{' '}
                {model.invoiceNumber || '—'}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            borderRadius: '16px',
            border: `1px solid ${BORDER}`,
            overflow: 'hidden',
            mb: 3,
            flexDirection: { xs: 'column', md: 'row' },
            boxShadow: '0 4px 20px rgba(15, 23, 42, 0.04)',
          }}
        >
          <AddressBlock
            name={model.companyName || PAYMENT_COMPANY.name}
            lines={[
              model.companyAddress,
              `Email: ${model.companyEmail}`,
              `MOB: ${model.companyPhone}`,
              `GSTIN: ${model.companyGstin}`,
            ]}
          />
          <Box sx={{ width: { xs: '100%', md: 1 }, bgcolor: BORDER, minHeight: { xs: 1, md: 'auto' } }} />
          <AddressBlock
            title="Bill To"
            name={model.billToName}
            lines={[model.billToAddress]}
            accent={ACCENT}
          />
        </Box>

        <Box
          sx={{
            borderRadius: '14px',
            border: `1px solid ${BORDER}`,
            overflow: 'hidden',
            mb: 2.5,
          }}
        >
          <Box
            component="table"
            sx={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '0.82rem',
              '& th': {
                bgcolor: ACCENT_HEADER,
                color: INK,
                fontWeight: 700,
                textAlign: 'left',
                px: 1.5,
                py: 1.25,
                borderBottom: `1px solid ${BORDER}`,
                whiteSpace: 'nowrap',
              },
              '& td': {
                px: 1.5,
                py: 1.35,
                borderBottom: `1px solid rgba(15, 23, 42, 0.06)`,
                color: SLATE,
                verticalAlign: 'top',
              },
              '& tr:last-child td': { borderBottom: 'none' },
            }}
          >
            <thead>
              <tr>
                <th style={{ width: 48 }}>No.</th>
                <th>Description</th>
                <th style={{ width: 90 }}>Duration</th>
                <th style={{ width: 110, textAlign: 'right' }}>Rate (GBP/day)</th>
                <th style={{ width: 120, textAlign: 'right' }}>Amount (GBP)</th>
              </tr>
            </thead>
            <tbody>
              {(model.lineItems || []).map((item, index) => (
                <tr key={item.id}>
                  <td>{index + 1}.</td>
                  <td style={{ color: INK, fontWeight: 500 }}>{item.description || '—'}</td>
                  <td>{item.durationDays ? `${item.durationDays} days` : '—'}</td>
                  <td style={{ textAlign: 'right' }}>{item.ratePerDay || '—'}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: INK }}>
                    {formatGbp(paymentLineAmount(item)).replace('£', '')}
                  </td>
                </tr>
              ))}
            </tbody>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              bgcolor: '#fafafa',
              borderTop: `1px solid ${BORDER}`,
            }}
          >
            <Box sx={{ minWidth: 220, p: 1.5 }}>
              {[
                ['Sub Total', subtotal, false],
                ['Total', total, false],
                ['Balance Due', total, true],
              ].map(([label, value, highlight]) => (
                <Box
                  key={label}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 3,
                    py: 0.65,
                    px: 1,
                    borderRadius: highlight ? '8px' : 0,
                    bgcolor: highlight ? ACCENT_HEADER : 'transparent',
                    fontWeight: highlight ? 800 : 600,
                  }}
                >
                  <Typography sx={{ fontSize: '0.82rem', color: highlight ? INK : SLATE }}>{label}</Typography>
                  <Typography sx={{ fontSize: '0.82rem', color: INK }}>
                    {formatGbp(value).replace('£', '')}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            p: 2,
            mb: 2.5,
            borderRadius: '14px',
            bgcolor: ACCENT_SOFT,
            border: `1px solid rgba(13, 148, 136, 0.18)`,
          }}
        >
          <Typography
            sx={{
              fontSize: '0.72rem',
              fontWeight: 800,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: ACCENT,
              mb: 1,
            }}
          >
            Amount chargeable (in words)
          </Typography>
          <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: INK, mb: 0.5 }}>
            GBP VALUE: {amountInWordsGbp(total)}
          </Typography>
          {inrAmount > 0 && (
            <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: INK }}>
              INR VALUE: {amountInWordsInr(inrAmount)}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            p: 2,
            mb: 2.5,
            borderRadius: '14px',
            border: `1px solid ${BORDER}`,
            bgcolor: '#fff',
          }}
        >
          <Typography
            sx={{
              fontSize: '0.72rem',
              fontWeight: 800,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: ACCENT,
              mb: 1.25,
            }}
          >
            Company&apos;s Bank Details
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1,
            }}
          >
            {[
              ['Bank Name', model.bankName],
              ['A/c No.', model.accountNumber],
              ['Branch & IFS Code', model.branchIfsc],
              ['Swift Code', model.swiftCode],
              ['AD Code', model.adCode],
              ['IE Code', model.ieCode],
            ].map(([label, value]) => (
              <Typography key={label} sx={{ fontSize: '0.8rem', color: SLATE }}>
                <Box component="span" sx={{ fontWeight: 700, color: INK }}>
                  {label}:
                </Box>{' '}
                {value || '—'}
              </Typography>
            ))}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2 }}>
          <Box sx={{ textAlign: 'center', minWidth: 200 }}>
            <Box sx={{ height: 56, borderBottom: `1px solid ${BORDER}`, mb: 1 }} />
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: INK }}>
              {model.authorizedSignatory || 'Authorized Signatory'}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
